import React, { useState, useEffect, useCallback } from 'react';
import { procurementService } from '../services/api';
import { fmtDateTime, getDaysUntil, formatNumeroCompraComAno, getOrgaoDisplay, getMonitoringPhase, getProrrogacao, isLicitacaoOver } from '../utils/formatters';
import { StatusBadge } from './StatusBadge';
import { CompraItem, getCompraItemBadge } from '../utils/procurementEnums';
import { PORTAL_MAP, portalLinkUrl } from './licitacoes/procurementUi';
import { useConfirmarPerigo } from './shared/ConfirmDialog';
import { campo } from '../design';
import { barraDoEstado } from './shared/lista/PecasDaLista';

interface MonitoringViewProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
  externalSearch?: string;
}

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    search: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    refresh: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    eye: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    chevronDown: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
    chevronUp: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>,
    chevronRight: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
    mail: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    plus: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
    download: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    trash: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    x: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
    check: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
    externalLink: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
  };
  return icons[name] || null;
};

// Padrão da Central (L3, 23/10/2026): andamento e fato consumado (perdido, desistida) são neutros; só a vitória com destaque.
const STATUS_COLORS: Record<string, string> = {
  participando: 'bg-slate-600/30 text-slate-300',
  interessado: 'bg-slate-600/30 text-slate-300',
  analisando: 'bg-slate-600/30 text-slate-300',
  ganho: 'bg-emerald-500/20 text-emerald-400',
  perdido: 'bg-slate-600/30 text-slate-300',
  desistida: 'bg-slate-600/30 text-slate-300',
};

// A LINHA POR FASE (L3, 23/10/2026, padrão da Central). Antes cada fase pintava o FUNDO da linha inteira (9 tons); a lista
// virava um arco-íris em que nada se destacava. Agora a linha é neutra e só a fase que pede atenção ganha a barra de 3px:
// Em disputa (acontecendo agora) e Suspensa em âmbar, Revogada em vermelho. O selo da coluna Status segue dizendo a fase.
const BARRA_DA_FASE: Record<string, 'warning' | 'danger'> = {
  em_disputa: 'warning',
  suspensa: 'warning',
  revogada: 'danger',
};

// PORTAL_MAP vem do procurementUi (fonte única, ~70 portais). Esta tela tinha a
// própria cópia parcial, e o efeito era o mesmo portal aparecer com badge aqui e
// slug cru na tela vizinha — dívida F-5 da auditoria de 13/08.

// A origem por PESO: o pregoeiro é quem decide, então se destaca em branco; sistema e fornecedor ficam neutros.
const ORIGIN_COLORS: Record<string, string> = {
  pregoeiro: 'bg-slate-600/40 text-white font-semibold',
  sistema: 'bg-slate-500/20 text-slate-400',
  fornecedor: 'bg-slate-500/20 text-slate-300',
};

const ORIGIN_LABELS: Record<string, string> = {
  pregoeiro: 'Pregoeiro',
  sistema: 'Sistema',
  fornecedor: 'Fornecedor',
};

const fmt = (v: number | null | undefined) =>
  v != null
    ? 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '\u2014';

const PNCP_URL_REGEX = /pncp\.gov\.br\/app\/editais\/(\d{14})\/(\d{4})\/(\d+)/;
const COMPRASNET_URL_REGEX = /[?&]compra=(\d{17,20})/;
const PORTAL_URL_PATTERNS = [PNCP_URL_REGEX, COMPRASNET_URL_REGEX];

const MonitoringView: React.FC<MonitoringViewProps> = ({ showToast, externalSearch }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<Record<number, { total: number; unread: number }>>({});
  const [searchQuery, setSearchQuery] = useState('');
  // R1.14: sincroniza com busca global do DisputeView quando embedded
  useEffect(() => {
    if (externalSearch !== undefined) setSearchQuery(externalSearch);
  }, [externalSearch]);
  const [statusFilter, setStatusFilter] = useState('');
  // Tab Ativas/Histórico — Ativas (default) esconde terminais (homologada/
  // encerrada/revogada/fracassada/desistida); Histórico mostra só terminais.
  const [tab, setTab] = useState<'ativas' | 'historico'>('ativas');
  const [checking, setChecking] = useState(false);
  // Bug HH: modal de progresso do Verificar
  const [batchModal, setBatchModal] = useState<null | {
    batchJobId: string;
    started: number;
  }>(null);
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const [monitorStatus, setMonitorStatus] = useState<Record<number, { tier: string; intervalLabel: string; lastMessageAt: string | null; lastCheckedAt: string | null }>>({});

  // Expanded row for messages
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [fetchingMessagesId, setFetchingMessagesId] = useState<number | null>(null);
  const [fetchElapsed, setFetchElapsed] = useState(0);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importItems, setImportItems] = useState<any[]>([]);
  const [importProposalItems, setImportProposalItems] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importSearch, setImportSearch] = useState('');
  const [importSelected, setImportSelected] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importTab, setImportTab] = useState<'novos' | 'propostas'>('novos');

  // Cadastrar modal state
  const [showCadastrarModal, setShowCadastrarModal] = useState(false);
  const [cadastrarTab, setCadastrarTab] = useState<'pncp' | 'manual'>('pncp');
  const [cadastrarUrl, setCadastrarUrl] = useState('');
  const [cadastrarCnpj, setCadastrarCnpj] = useState('');
  const [cadastrarAno, setCadastrarAno] = useState(new Date().getFullYear().toString());
  const [cadastrarSeq, setCadastrarSeq] = useState('');
  const [cadastrarLoading, setCadastrarLoading] = useState(false);
  const [cadastrarError, setCadastrarError] = useState('');
  // Manual registration state
  const [manualModalidade, setManualModalidade] = useState<'pe' | 'de'>('pe');
  const [manualUasg, setManualUasg] = useState('');
  const [manualNumero, setManualNumero] = useState('');
  const [manualOrgao, setManualOrgao] = useState('');
  const [manualDescricao, setManualDescricao] = useState('');
  const [manualValor, setManualValor] = useState('');
  const [manualAbertura, setManualAbertura] = useState('');
  const [manualEncerramento, setManualEncerramento] = useState('');
  const [manualSrp, setManualSrp] = useState(false);
  const [manualProcessoSei, setManualProcessoSei] = useState('');
  const [manualCriterio, setManualCriterio] = useState('');
  const [manualLookupLoading, setManualLookupLoading] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('dataEncerramento');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const loadItems = useCallback(async () => {
    try {
      const data = await procurementService.getMonitoredItems();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Erro ao carregar itens monitorados:', e);
    }
    setLoading(false);
  }, []);

  const loadMonitorStatus = useCallback(async () => {
    try {
      const data = await procurementService.getMonitoringStatus();
      const map: Record<number, any> = {};
      if (Array.isArray(data)) {
        for (const r of data) map[r.itemId] = r;
      }
      setMonitorStatus(map);
    } catch {
      // silent
    }
  }, []);

  const loadUnreadCounts = useCallback(async () => {
    try {
      const data = await procurementService.getUnreadMessageCounts();
      const map: Record<number, { total: number; unread: number }> = {};
      if (Array.isArray(data)) {
        for (const r of data) map[r.itemId] = { total: r.total ?? r.count ?? 0, unread: r.unread ?? r.count ?? 0 };
      }
      setUnreadCounts(map);
    } catch {
      // silent
    }
  }, []);

  const loadMessages = useCallback(async (itemId: number) => {
    setMessagesLoading(true);
    try {
      const msgs = await procurementService.getItemMessages(itemId);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      setMessages([]);
    }
    setMessagesLoading(false);
  }, []);

  const markRead = useCallback(async (itemId: number) => {
    try {
      await procurementService.markMessagesRead(itemId);
      setUnreadCounts(prev => ({ ...prev, [itemId]: { total: prev[itemId]?.total || 0, unread: 0 } }));
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadUnreadCounts();
    loadMonitorStatus();
  }, [loadItems, loadUnreadCounts, loadMonitorStatus]);

  // Bug HH-2 (26/05/2026): detecta batch ativo no mount + a cada 30s.
  // Permite reabrir o modal depois de fechar (botão Verificando vira clicável)
  // e mostra resumo de batches recém-finalizados.
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const checkActiveBatch = useCallback(async () => {
    try {
      const data = await procurementService.getActiveBatch();
      if (data?.found) {
        setActiveBatchId(data.batchJobId);
        if (data.active) setChecking(true);
      } else {
        setActiveBatchId(null);
      }
    } catch {
      // silent
    }
  }, []);
  useEffect(() => {
    checkActiveBatch();
    const interval = setInterval(checkActiveBatch, 30000);
    return () => clearInterval(interval);
  }, [checkActiveBatch]);

  // Auto-refresh badges + intervalo (tier) a cada 60s. Sem isso a coluna
  // "Intervalo" ficava stale: msgs novas chegavam pelo cron mas tier no UI
  // continuava no valor antigo (ex: "4h" mesmo após daysSinceMsg cair < 3).
  useEffect(() => {
    const interval = setInterval(() => {
      loadUnreadCounts();
      loadMonitorStatus();
      // F-8/F-6 (Onda 2): recarrega TAMBÉM os itens — status/situação mudados
      // pelos crons (funil de terminalização, prorrogação) só apareciam num
      // reload manual da página; o interval só atualizava badges/tier.
      loadItems();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadUnreadCounts, loadMonitorStatus, loadItems]);

  const handleToggleExpand = useCallback((item: any) => {
    if (expandedItemId === item.id) {
      setExpandedItemId(null);
      setMessages([]);
    } else {
      setExpandedItemId(item.id);
      loadMessages(item.id);
      if (unreadCounts[item.id]?.unread > 0) {
        markRead(item.id);
      }
    }
  }, [expandedItemId, loadMessages, markRead, unreadCounts]);

  const handleCheckMonitoring = async () => {
    setChecking(true);
    try {
      const result = await procurementService.checkMonitoring();
      if (result?.batchJobId) {
        // Bug HH: abre modal de progresso pra acompanhar
        setBatchModal({ batchJobId: result.batchJobId, started: Date.now() });
        setBatchStatus(null);
      } else if (result?.skippedReason === 'already_running') {
        // Cron natural rodando — tentar achar o batch ativo recente e abrir modal nele
        showToast('Verificação já em andamento (cron natural). Acompanhe na próxima janela.');
        setChecking(false);
      } else if (result?.queued === 0) {
        const skippedTier = result?.skipped || 0;
        const totalItems = result?.totalItems || 0;
        if (totalItems > 0 && skippedTier === totalItems) {
          showToast(`Todos os ${totalItems} items foram checados recentemente (tier). Aguarde o próximo cron.`);
        } else {
          showToast('Nenhum item elegível agora');
        }
        setChecking(false);
      } else {
        showToast('Verificação iniciada em background');
        setTimeout(() => { loadItems(); loadUnreadCounts(); setChecking(false); }, 5000);
      }
    } catch {
      showToast('Erro ao verificar', 'error');
      setChecking(false);
    }
  };

  // Bug HH: polling do batch status quando modal aberto
  useEffect(() => {
    if (!batchModal) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await procurementService.getBatchStatus(batchModal.batchJobId);
        if (!cancelled) setBatchStatus(data);
        if (data?.finishedAt) {
          // Recarrega items, msgs e tier pra mostrar contagens novas + intervalo correto
          await loadItems();
          await loadUnreadCounts();
          await loadMonitorStatus();
          return;
        }
      } catch {
        // continua tentando
      }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [batchModal, loadItems, loadUnreadCounts, loadMonitorStatus]);

  // ==================== Fetch Messages ====================

  const handleFetchMessages = async (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    if (fetchingMessagesId) return;
    setFetchingMessagesId(item.id);
    setFetchElapsed(0);

    const timerInterval = setInterval(() => setFetchElapsed((p) => p + 1), 1000);

    try {
      const result = await procurementService.scrapeItemNow(item.id);
      clearInterval(timerInterval);
      setFetchingMessagesId(null);
      setFetchElapsed(0);

      if (!result?.ok) {
        showToast(result?.error || 'Erro ao buscar mensagens', 'error');
        return;
      }

      // Recarrega msgs + counts + items (status pode ter mudado)
      if (expandedItemId === item.id) await loadMessages(item.id);
      await Promise.all([loadUnreadCounts(), loadItems()]);

      const parts: string[] = [];
      if (result.saved > 0) parts.push(`${result.saved} nova(s)`);
      if (result.statusChange) {
        parts.push(`status: ${result.statusChange.from} → ${result.statusChange.to}`);
      }
      showToast(parts.length ? `Atualizado: ${parts.join(', ')}` : 'Sem novidades', 'success');
    } catch (e: any) {
      clearInterval(timerInterval);
      setFetchingMessagesId(null);
      setFetchElapsed(0);
      const msg = e?.response?.data?.message || e?.message || 'Erro ao buscar mensagens';
      showToast(typeof msg === 'string' ? msg : 'Erro ao buscar mensagens', 'error');
    }
  };

  // ==================== Import from Banco ====================

  const handleOpenImport = async () => {
    setShowImportModal(true);
    setImportLoading(true);
    setImportSearch('');
    setImportSelected(new Set());
    setImportTab('novos');
    try {
      const [novosData, proposalsData] = await Promise.all([
        procurementService.getItems({ status: 'novo' }),
        procurementService.getProposals(),
      ]);
      const novosArr = Array.isArray(novosData) ? novosData : (novosData?.data || []);
      setImportItems(novosArr);

      const proposalsArr = Array.isArray(proposalsData) ? proposalsData : [];
      const seen = new Set<number>();
      const monitoredIds = new Set(items.map((i: any) => i.id));
      const proposalItemsList: any[] = [];
      for (const p of proposalsArr) {
        if (p.item && !seen.has(p.item.id) && !monitoredIds.has(p.item.id)) {
          seen.add(p.item.id);
          proposalItemsList.push({ ...p.item, _proposalStatus: p.status, _proposalValue: p.totalValue });
        }
      }
      setImportProposalItems(proposalItemsList);
    } catch {
      setImportItems([]);
      setImportProposalItems([]);
      showToast('Erro ao carregar itens do banco', 'error');
    }
    setImportLoading(false);
  };

  const toggleImportSelect = (id: number) => {
    setImportSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImportSelected = async () => {
    if (importSelected.size === 0) return;
    setImporting(true);
    let success = 0;
    for (const id of Array.from(importSelected)) {
      try {
        await procurementService.toggleItemMonitored(id, true);
        await procurementService.updateItemStatus(id, { status: 'participando' });
        success++;
      } catch {
        // continue
      }
    }
    setImporting(false);
    setShowImportModal(false);
    showToast(`${success} item(s) importado(s) para monitoramento`);
    loadItems();
  };

  const activeImportList = importTab === 'novos' ? importItems : importProposalItems;
  const filteredImportItems = activeImportList.filter(item => {
    if (!importSearch) return true;
    const q = importSearch.toLowerCase();
    return (item.orgaoNome || '').toLowerCase().includes(q) ||
           (item.unidadeNome || '').toLowerCase().includes(q) ||
           (item.objetoCompra || '').toLowerCase().includes(q);
  });

  // ==================== Cadastrar (PNCP Lookup) ====================

  const isPortalUrl = (url: string) => PORTAL_URL_PATTERNS.some(r => r.test(url));
  const isPncpUrl = (url: string) => PNCP_URL_REGEX.test(url);

  const handleCadastrarUrlChange = (val: string) => {
    setCadastrarUrl(val);
    setCadastrarError('');
    const pncpMatch = val.match(PNCP_URL_REGEX);
    if (pncpMatch) {
      setCadastrarCnpj(pncpMatch[1]);
      setCadastrarAno(pncpMatch[2]);
      setCadastrarSeq(pncpMatch[3]);
    }
  };

  const handleCadastrar = async () => {
    const urlVal = cadastrarUrl.trim();
    const hasPortalUrl = urlVal && isPortalUrl(urlVal);

    if (hasPortalUrl && !isPncpUrl(urlVal)) {
      setCadastrarLoading(true);
      setCadastrarError('');
      try {
        await procurementService.importFromPncp({ url: urlVal });
        showToast('Licitacao cadastrada com sucesso');
        setShowCadastrarModal(false);
        setCadastrarUrl('');
        setCadastrarCnpj('');
        setCadastrarAno(new Date().getFullYear().toString());
        setCadastrarSeq('');
        loadItems();
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || 'Erro ao cadastrar';
        setCadastrarError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setCadastrarLoading(false);
      return;
    }

    const cnpj = cadastrarCnpj.replace(/\D/g, '');
    const ano = parseInt(cadastrarAno, 10);
    const seq = parseInt(cadastrarSeq, 10);

    if (!cnpj || cnpj.length !== 14) {
      setCadastrarError('CNPJ deve ter 14 digitos');
      return;
    }
    if (!ano || ano < 2000) {
      setCadastrarError('Ano invalido');
      return;
    }
    if (!seq || seq < 1) {
      setCadastrarError('Sequencial invalido');
      return;
    }

    setCadastrarLoading(true);
    setCadastrarError('');
    try {
      await procurementService.importFromPncp({ cnpjOrgao: cnpj, ano, sequencial: seq });
      showToast('Licitacao cadastrada com sucesso');
      setShowCadastrarModal(false);
      setCadastrarUrl('');
      setCadastrarCnpj('');
      setCadastrarAno(new Date().getFullYear().toString());
      setCadastrarSeq('');
      loadItems();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Erro ao cadastrar';
      setCadastrarError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setCadastrarLoading(false);
  };

  const formatNumeroCompra = (val: string): string => {
    if (val.includes('/')) return val;
    const digits = val.replace(/\D/g, '');
    if (digits.length >= 9) {
      const year = digits.slice(-4);
      const num = digits.slice(0, -4);
      const y = parseInt(year, 10);
      if (y >= 2000 && y <= 2099) return `${num}/${year}`;
    }
    return val;
  };

  const handleManualNumeroChange = (val: string) => {
    setManualNumero(val);
    setCadastrarError('');
  };

  const handleManualNumeroBlur = () => {
    const formatted = formatNumeroCompra(manualNumero);
    if (formatted !== manualNumero) setManualNumero(formatted);
    // Trigger lookup when both fields are filled
    if (manualUasg.trim().length >= 5 && formatted.includes('/')) {
      doManualLookup(manualUasg.trim(), formatted);
    }
  };

  const handleManualUasgBlur = () => {
    const uasg = manualUasg.trim();
    if (uasg.length >= 5) {
      const numero = formatNumeroCompra(manualNumero);
      doManualLookup(uasg, numero.includes('/') ? numero : undefined);
    }
  };

  const doManualLookup = async (uasg: string, numero?: string) => {
    setManualLookupLoading(true);
    try {
      const result = await procurementService.lookupUasg(uasg, numero);
      if (result) {
        if (!manualOrgao) {
          const display = (result as any).unidadeNome || result.orgaoNome;
          if (display) setManualOrgao(display);
        }
        if (!manualDescricao && result.objetoCompra) setManualDescricao(result.objetoCompra);
        if (!manualValor && result.valorEstimado) setManualValor(String(result.valorEstimado));
        if (!manualAbertura && result.dataAbertura) {
          // datetime-local input expects YYYY-MM-DDTHH:mm
          const dt = result.dataAbertura;
          setManualAbertura(dt.includes('T') ? dt.slice(0, 16) : dt + 'T00:00');
        }
        if (!manualEncerramento && result.dataEncerramento) {
          const dt = result.dataEncerramento;
          setManualEncerramento(dt.includes('T') ? dt.slice(0, 16) : dt + 'T00:00');
        }
        if (result.srp !== undefined) setManualSrp(result.srp);
      }
    } catch {
      // silent
    }
    setManualLookupLoading(false);
  };

  const handleCadastrarManual = async () => {
    const uasg = manualUasg.trim();
    const numero = formatNumeroCompra(manualNumero.trim());

    if (!uasg) {
      setCadastrarError('UASG e obrigatorio');
      return;
    }
    if (!numero) {
      setCadastrarError('Numero da compra e obrigatorio');
      return;
    }

    // Update display with formatted value
    if (numero !== manualNumero) setManualNumero(numero);

    setCadastrarLoading(true);
    setCadastrarError('');
    try {
      await procurementService.registerManual({
        portal: 'comprasnet',
        modalidade: manualModalidade,
        codigoUnidade: uasg,
        numeroCompra: numero,
        orgaoNome: manualOrgao.trim() || undefined,
        objetoCompra: manualDescricao.trim() || undefined,
        valorEstimado: manualValor ? parseFloat(manualValor) : undefined,
        dataAbertura: manualAbertura || undefined,
        dataEncerramento: manualEncerramento || undefined,
        srp: manualSrp,
        processoSei: manualProcessoSei.trim() || undefined,
        criterioJulgamento: manualCriterio.trim() || undefined,
      });
      showToast('Licitacao cadastrada com sucesso');
      setShowCadastrarModal(false);
      setManualModalidade('pe');
      setManualUasg('');
      setManualNumero('');
      setManualOrgao('');
      setManualDescricao('');
      setManualValor('');
      setManualAbertura('');
      setManualProcessoSei('');
      setManualCriterio('');
      setManualEncerramento('');
      setManualSrp(false);
      setCadastrarTab('pncp');
      loadItems();
      loadMonitorStatus();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Erro ao cadastrar';
      setCadastrarError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setCadastrarLoading(false);
  };

  // ==================== Discard ====================

  const handleDiscard = async (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    if (!await confirmarPerigo(`Descartar "${(getOrgaoDisplay(item) || 'item').substring(0, 60)}" do monitoramento?`)) return;
    try {
      await procurementService.deleteItem(item.id);
      showToast('Item descartado');
      if (expandedItemId === item.id) {
        setExpandedItemId(null);
        setMessages([]);
      }
      loadItems();
    } catch {
      showToast('Erro ao descartar', 'error');
    }
  };

  const handleChangeStatus = async (e: React.ChangeEvent<HTMLSelectElement>, item: any) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === item.status) return;
    try {
      await procurementService.updateItemStatus(item.id, { status: newStatus });
      showToast(`Status alterado para ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
      loadItems();
      loadMonitorStatus();
    } catch {
      showToast('Erro ao alterar status', 'error');
    }
  };

  // ==================== Sorting ====================

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  // ==================== Filtering & Sorting ====================

  const filteredItems = items
    .filter(item => {
      const phase = getMonitoringPhase(item);
      // Tab: Ativas esconde terminais; Histórico mostra só terminais
      if (tab === 'ativas' && phase.isTerminal) return false;
      if (tab === 'historico' && !phase.isTerminal) return false;
      if (statusFilter && item.status !== statusFilter) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (item.orgaoNome || '').toLowerCase().includes(q) ||
             (item.unidadeNome || '').toLowerCase().includes(q) ||
             (item.objetoCompra || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const dir = sortOrder === 'ASC' ? 1 : -1;
      const va = a[sortBy];
      const vb = b[sortBy];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      if (typeof va === 'number') return (va - vb) * dir;
      return (new Date(va).getTime() - new Date(vb).getTime()) * dir;
    });

  // ==================== Metrics ====================

  const metrics = {
    total: items.length,
    participando: items.filter(i => i.status === 'participando'),
    ganho: items.filter(i => i.status === 'ganho'),
    perdido: items.filter(i => i.status === 'perdido'),
    totalUnread: Object.values(unreadCounts).reduce((s, c) => s + c.unread, 0),
    participandoUnread: items.filter(i => i.status === 'participando').reduce((s, i) => s + (unreadCounts[i.id]?.unread || 0), 0),
    ganhoUnread: items.filter(i => i.status === 'ganho').reduce((s, i) => s + (unreadCounts[i.id]?.unread || 0), 0),
    perdidoUnread: items.filter(i => i.status === 'perdido').reduce((s, i) => s + (unreadCounts[i.id]?.unread || 0), 0),
  };

  // ==================== Render helpers ====================

  const SortHeader = ({ column, label, className = '' }: { column: string; label: string; className?: string }) => (
    <th
      className={`px-3 py-2 text-xs font-medium uppercase cursor-pointer select-none hover:text-white ${sortBy === column ? 'text-white' : 'text-slate-400'} ${className}`}
      aria-sort={sortBy === column ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : undefined}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1 justify-inherit">
        {label}
        {sortBy === column ? (
          <span className="text-white">{sortOrder === 'ASC' ? '\u25B2' : '\u25BC'}</span>
        ) : (
          <span className="text-slate-600">{'\u21C5'}</span>
        )}
      </div>
    </th>
  );

  const renderMessageCard = (msg: any) => {
    const badgeClass = ORIGIN_COLORS[msg.origin] || 'bg-slate-500/20 text-slate-400';
    const label = ORIGIN_LABELS[msg.origin] || msg.origin;
    const isIpcom = /ipcom|06\.?163\.?889/i.test(msg.messageText || '');

    return (
      <div key={msg.id} className={`rounded-lg p-3 ${isIpcom ? 'border-l-4 border-yellow-500 bg-yellow-500/10' : 'bg-central-elevado'}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badgeClass}`}>
            {label}
          </span>
          {isIpcom && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-yellow-500/20 text-yellow-400 animate-pulse">
              IPCOM
            </span>
          )}
          {msg.itemReference && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-slate-600/30 text-slate-300">
              {msg.itemReference}
            </span>
          )}
          <span className="text-xs text-slate-500 ml-auto">{fmtDateTime(msg.messageDate)}</span>
        </div>
        <p className="text-sm text-slate-200 whitespace-pre-wrap">{msg.messageText}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-400 animate-pulse">Carregando...</div>
    );
  }

  return (
    <div>
      {/* Actions bar */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => { setShowCadastrarModal(true); setCadastrarError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
        >
          <Icon name="plus" className="w-4 h-4" /> Cadastrar
        </button>
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por orgao, objeto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full rounded-lg pl-9 pr-3 py-2 text-sm placeholder-slate-400 ${campo}`}
          />
        </div>
        <button
          onClick={handleOpenImport}
          className="flex items-center gap-2 px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
        >
          <Icon name="download" className="w-4 h-4" /> Importar
        </button>
        <button
          onClick={() => {
            // Se há batch ativo, REABRE o modal em vez de criar novo
            if (activeBatchId && !batchModal) {
              setBatchModal({ batchJobId: activeBatchId, started: Date.now() });
              setBatchStatus(null);
              return;
            }
            if (!checking) handleCheckMonitoring();
          }}
          disabled={checking && !activeBatchId}
          className="flex items-center gap-2 px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 rounded-lg text-sm transition-colors"
          title={activeBatchId ? 'Há verificação em andamento — clique pra ver progresso' : undefined}
        >
          <Icon name="refresh" className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Verificando...' : 'Verificar'}
        </button>
      </div>

      {/* Tabs Ativas / Histórico */}
      <div className="flex items-center gap-2 mb-3">
        {(() => {
          const counts = items.reduce(
            (acc, it) => {
              const p = getMonitoringPhase(it);
              if (p.isTerminal) acc.historico++; else acc.ativas++;
              return acc;
            },
            { ativas: 0, historico: 0 },
          );
          return (
            <>
              <button
                onClick={() => setTab('ativas')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'ativas' ? 'bg-central-elevado text-white font-semibold border border-central-borda' : 'bg-central-cartao text-slate-300 hover:bg-central-elevado border border-central-borda'}`}
              >
                Ativas ({counts.ativas})
              </button>
              <button
                onClick={() => setTab('historico')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'historico' ? 'bg-central-elevado text-white font-semibold border border-central-borda' : 'bg-central-cartao text-slate-300 hover:bg-central-elevado border border-central-borda'}`}
              >
                Histórico ({counts.historico})
              </button>
            </>
          );
        })()}
      </div>

      {/* Summary bar */}
      {/* Fase 2 padrão visual: superfície /50 + acentuação */}
      <div className="flex items-center gap-4 mb-4 bg-central-cartao rounded-central p-4 border border-central-borda">
        <div className="flex items-center gap-2">
          <Icon name="eye" className="w-5 h-5 text-slate-400" />
          <span className="text-white font-bold text-lg">{metrics.total}</span>
          <span className="text-slate-400 text-sm">{metrics.total === 1 ? 'licitação monitorada' : 'licitações monitoradas'}</span>
        </div>
        {metrics.totalUnread > 0 && (
          // U2 (Heitor, 24/10/2026): neutro, o número em negrito. Quase toda linha tem não lida; âmbar em tudo parava de avisar.
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-600/30 text-slate-300 text-xs font-medium" data-nao-lidas>
            <Icon name="mail" className="w-3.5 h-3.5" />
            <b className="text-white">{metrics.totalUnread}</b> {metrics.totalUnread === 1 ? 'mensagem não lida' : 'mensagens não lidas'}
          </span>
        )}
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Icon name="eye" className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium mb-1">Nenhuma licitacao em andamento</p>
          <p className="text-sm mb-4">Importe licitacoes do Banco ou cadastre manualmente para monitorar</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => { setShowCadastrarModal(true); setCadastrarError(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
            >
              <Icon name="plus" className="w-4 h-4" /> Cadastrar Licitacao
            </button>
            <button
              onClick={handleOpenImport}
              className="flex items-center gap-2 px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
            >
              <Icon name="download" className="w-4 h-4" /> Importar do Banco
            </button>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 text-slate-400">Nenhum item encontrado</div>
      ) : (
        <div className="bg-central-cartao rounded-central border border-central-borda overflow-x-auto">
          {/* overflow-x-auto (08/08): sem ele a tabela CORTAVA as colunas da
              direita (Ações) em zoom/tela menor, sem rolagem possível — conteúdo
              largo deve rolar dentro do próprio container, nunca sumir. */}
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-central-fundo/95 border-b border-central-borda">
                {/* UX 08/08: 14 → 10 colunas. N./Cód./Mod./SRP viraram sub-linha da
                    própria Licitação — a coluna principal truncava em ~10 chars
                    ("PODER JUD...") enquanto 4 colunas de metadado ocupavam ~250px. */}
                <th className="px-3 py-2 w-8" />
                <SortHeader column="orgaoNome" label="Licitação" className="text-left" />
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-400 uppercase w-24">Portal</th>
                <SortHeader column="valorEstimado" label="Valor" className="text-right w-28" />
                <SortHeader column="status" label="Status" className="text-center w-24" />
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-400 uppercase w-14">Msgs</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-400 uppercase w-24">Intervalo</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-400 uppercase w-28">Abertura</th>
                <SortHeader column="dataEncerramento" label="Proposta / Sessão" className="text-left w-44" />
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-400 uppercase w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-central-borda">
              {filteredItems.map((item: any) => {
                const statusClass = STATUS_COLORS[item.status] || 'bg-slate-600/30 text-slate-300';
                const barraDaFase = BARRA_DA_FASE[getMonitoringPhase(item).key] || null;
                const isExpanded = expandedItemId === item.id;
                const msgData = unreadCounts[item.id];
                const unread = msgData?.unread || 0;
                const totalMsgs = msgData?.total || 0;
                const modBadge = item.modalidadeNome?.includes('Pregao') || item.modalidadeNome?.includes('Pregão') ? 'PE'
                  : item.modalidadeNome?.includes('Dispensa') ? 'DE'
                  : item.modalidadeNome?.includes('Concorrência') || item.modalidadeNome?.includes('Concorrencia') ? 'CC'
                  : item.modalidadeNome ? item.modalidadeNome.substring(0, 4) : '';

                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className="hover:bg-central-elevado cursor-pointer"
                      data-fase={getMonitoringPhase(item).key}
                      onClick={() => handleToggleExpand(item)}
                    >
                      {/* Expand chevron — e a barra da fase (só a que pede atenção) */}
                      <td className={`px-2 py-2 text-center ${barraDoEstado(barraDaFase)}`}>
                        <Icon
                          name={isExpanded ? 'chevronDown' : 'chevronRight'}
                          className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'text-white' : 'text-slate-500'}`}
                        />
                      </td>

                      {/* Licitacao */}
                      <td className="px-3 py-2 max-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-slate-200 text-xs truncate flex-1 min-w-0" title={getOrgaoDisplay(item)}>{getOrgaoDisplay(item) || '\u2014'}</div>
                          {(() => {
                            // ComprasNet seta permitirInclusao=false para qualquer evento
                            // pendente (Alteração, Esclarecimento, Suspensão, julgamento pós-
                            // prazo, etc.). Só Suspensão/Revogação/Anulação bloqueiam de fato
                            // — alinhado com isSituacaoBlockingEvent em LicitacoesTab.
                            const sc = item.situacaoCompra;
                            // Evento pendente de publicação (possuiAvisoDeEvento) — pode ter
                            // permitirInclusao=true; provável adiamento/suspensão.
                            if (sc?.eventoPendente === true) {
                              return (
                                <span
                                  className="text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap cursor-help flex-shrink-0 bg-amber-500/20 text-amber-400"
                                  title={sc.ultimoAviso?.mensagem || 'Evento pendente de publicação — provável adiamento/suspensão, não enviar proposta'}
                                  onClick={e => e.stopPropagation()}
                                >
                                  ⚠ Evento pendente
                                </span>
                              );
                            }
                            if (!sc || sc.permitirInclusao !== false) return null;
                            const msg = sc.ultimoAviso?.mensagem || '';
                            if (!/suspen|revogad|anulad/i.test(msg)) return null;
                            const reabertura = sc.ultimoAviso?.previsaoReabertura;
                            // Vencida: previsão já passou mas portal ainda bloqueia
                            let overdue = false;
                            if (reabertura) {
                              const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(reabertura);
                              if (m) {
                                const [, dd, mo, yy] = m;
                                const endOfDayUtc = Date.UTC(Number(yy), Number(mo) - 1, Number(dd) + 1, 3, 0, 0);
                                overdue = Date.now() > endOfDayUtc;
                              }
                            }
                            const label = overdue
                              ? (reabertura ? `⛔ Venc. ${reabertura.slice(0, 5)}` : '⛔ Vencida')
                              : (reabertura ? `⚠ Suspensa — reabre ${reabertura.slice(0, 5)}` : '⚠ Suspensa');
                            return (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap cursor-help flex-shrink-0 ${
                                  overdue ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                                }`}
                                title={msg || 'Cadastro bloqueado pelo ComprasNet'}
                                onClick={e => e.stopPropagation()}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-slate-500 text-[10px] truncate" title={item.objetoCompra}>{item.objetoCompra || ''}</div>
                        {/* UX 08/08: N./Cód./Mod./SRP fundidos aqui (eram 4 colunas) */}
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-400">
                          {modBadge && (
                            <span className={`px-1 py-0 rounded ${modBadge === 'PE' ? 'bg-blue-500/20 text-blue-300' : modBadge === 'DE' ? 'bg-green-500/20 text-green-300' : 'bg-slate-600/50 text-slate-300'}`}>{modBadge}</span>
                          )}
                          {item.numeroCompra && <span className="text-slate-300">{formatNumeroCompraComAno(item.numeroCompra, item.anoCompra)}</span>}
                          {item.codigoUnidade && <span title="UASG / código da unidade">· {item.codigoUnidade}</span>}
                          {item.srp === true && <span className="px-1 py-0 rounded bg-slate-600/30 text-slate-300" title="Sistema de Registro de Preços">SRP</span>}
                        </div>
                      </td>

                      {/* Portal */}
                      <td className="px-3 py-2 text-center">
                        {item.portalOrigem && PORTAL_MAP[item.portalOrigem] ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${PORTAL_MAP[item.portalOrigem].color}`}>{PORTAL_MAP[item.portalOrigem].label}</span>
                        ) : <span className="text-[10px] text-slate-500">{item.portalOrigem || '\u2014'}</span>}
                      </td>

                      {/* Valor — valorEstimado do edital com fallback pra
                          proposalTotal (soma da proposta cadastrada) quando
                          portal não trouxe (caso BLL). Marca com asterisco
                          quando o valor é da proposta, não do edital. */}
                      <td className="px-3 py-2 text-right text-xs text-white font-medium">
                        {item.valorEstimado ? (
                          fmt(item.valorEstimado)
                        ) : item.proposalTotal ? (
                          <span title="Valor da proposta cadastrada (edital sem valor de referência)">
                            {fmt(item.proposalTotal)}<span className="text-slate-400 ml-0.5">*</span>
                          </span>
                        ) : (
                          fmt(item.valorEstimado)
                        )}
                      </td>

                      {/* Status — fase derivada (combina status + datas + session) */}
                      <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const phase = getMonitoringPhase(item);
                          return (
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${phase.color}`}
                              title={`Status interno: ${(item.status || '—').toUpperCase()}`}
                            >
                              {phase.label}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Msgs */}
                      <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                        {totalMsgs > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            {unread > 0 && <span className="bg-slate-600/30 text-white rounded-full text-[10px] font-bold px-1.5 py-0.5" data-nao-lidas>{unread}</span>}
                            <span className="text-[10px] text-slate-500">{unread > 0 ? '/' : ''}{totalMsgs}</span>
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-600">0</span>
                        )}
                      </td>

                      {/* Intervalo */}
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          const ms = monitorStatus[item.id];
                          if (!ms) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/30 text-slate-500 cursor-default" title="Monitoramento de mensagens desligado">desligado</span>;
                          const tierColors: Record<string, string> = {
                            active: 'bg-slate-600/30 text-slate-300',
                            'idle_3-7d': 'bg-yellow-500/20 text-yellow-400',
                            'idle_7-30d': 'bg-amber-500/20 text-amber-400',
                            no_messages: 'bg-slate-500/20 text-slate-400',
                            'idle_30d+': 'bg-red-500/20 text-red-400',
                            terminal: 'bg-slate-600/30 text-slate-500',
                          };
                          const colorClass = tierColors[ms.tier] || 'bg-slate-600/30 text-slate-400';
                          const lastChecked = ms.lastCheckedAt
                            ? fmtDateTime(ms.lastCheckedAt)
                            : 'nunca';
                          // F-8 (Onda 2): IDADE do último check NA LINHA (o "chk Xh" que o
                          // Encontrar já tem). Antes só aparecia no tooltip/drawer — itens
                          // com semanas sem verificação passavam despercebidos na lista.
                          const ageH = ms.lastCheckedAt
                            ? (Date.now() - new Date(ms.lastCheckedAt).getTime()) / 3600000
                            : Infinity;
                          const chkLabel = !ms.lastCheckedAt ? 'chk nunca'
                            : ageH < 1 ? `chk ${Math.max(1, Math.round(ageH * 60))}min`
                            : ageH < 48 ? `chk ${Math.round(ageH)}h`
                            : `chk ${Math.round(ageH / 24)}d`;
                          const chkColor = ageH > 168 ? 'bg-red-500/20 text-red-400'
                            : ageH > 48 ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-slate-600/30 text-slate-400';
                          return (
                            <span className="inline-flex flex-col items-center gap-0.5">
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded cursor-default ${colorClass}`}
                                title={`Tier: ${ms.tier} | Ultima verificacao: ${lastChecked}`}
                              >
                                {ms.intervalLabel}
                              </span>
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded cursor-default font-mono ${chkColor}`}
                                title={`Última verificação no portal: ${lastChecked}`}
                              >
                                {chkLabel}
                              </span>
                            </span>
                          );
                        })()}
                      </td>

                      {/* Abertura */}
                      <td className="px-3 py-2 text-center text-[10px] text-slate-300 whitespace-nowrap">{fmtDateTime(item.dataAbertura)}</td>

                      {/* Proposta / Sessão — 2 janelas distintas (ComprasNet dispensa):
                          Proposta (entrega) = dataEncerramento; Sessão/Lances = dataSessaoInicio→Fim. */}
                      <td className="px-3 py-2 text-left text-[10px] text-slate-300 whitespace-nowrap">
                        {(() => {
                          const enc = item.dataEncerramento ? new Date(item.dataEncerramento) : null;
                          const ss = item.dataSessaoInicio ? new Date(item.dataSessaoInicio) : null;
                          const sf = item.dataSessaoFim ? new Date(item.dataSessaoFim) : null;
                          const fmtHM = (x: Date) => x.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
                          const fmtDM = (x: Date) => x.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
                          const prorrog = getProrrogacao(item);
                          const showProrrog = !!prorrog && !isLicitacaoOver(item);
                          const days = getDaysUntil(item.dataEncerramento);
                          if (!enc && !ss) return <span className="text-slate-600">—</span>;
                          return (
                            <div className="flex flex-col items-start gap-0.5">
                              {enc && (
                                <span title="Prazo para envio da proposta">📄 até {fmtDM(enc)} {fmtHM(enc)}
                                  {days !== null && days < 0 && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-red-500/20 text-red-400">Encerrada</span>}
                                  {days === 0 && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-red-500/20 text-red-400">Hoje</span>}
                                  {days !== null && days > 0 && days <= 3 && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-slate-600/30 text-slate-300">{days}d</span>}
                                </span>
                              )}
                              {ss && <span className="text-slate-200" title="Sessão pública / envio de lances">⚖️ Sessão {fmtDM(ss)} {fmtHM(ss)}{sf ? `–${fmtHM(sf)}` : ''}</span>}
                              {showProrrog && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40 self-start" title={`Prorrogada: ${fmtDateTime(prorrog!.from)} → ${fmtDateTime(prorrog!.to)}`}>⏱ Prorrogada</span>}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Acoes */}
                      <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {(() => {
                            const link = portalLinkUrl(item);
                            if (!link) return null;
                            return (
                              <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-white transition-colors" title={link.title}>
                                <Icon name="externalLink" className="w-3.5 h-3.5" />
                              </a>
                            );
                          })()}
                          <button
                            onClick={(e) => handleFetchMessages(e, item)}
                            disabled={fetchingMessagesId === item.id}
                            className="p-1 text-slate-400 hover:text-white disabled:opacity-50 transition-colors"
                            title="Buscar mensagens do portal"
                          >
                            <Icon name="mail" className={`w-3.5 h-3.5 ${fetchingMessagesId === item.id ? 'animate-pulse' : ''}`} />
                          </button>
                          <button
                            onClick={(e) => handleDiscard(e, item)}
                            className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                            title="Descartar"
                          >
                            <Icon name="trash" className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Messages Row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          {/* Drawer lateral (29/06) — mesma abordagem do Cadastrar:
                              abre painel fixo à direita em vez de expandir inline. */}
                          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setExpandedItemId(null)} />
                          <div className="fixed inset-y-0 right-0 z-50 w-full lg:w-[62vw] max-w-[1180px] bg-central-cartao border-l border-central-borda shadow-2xl flex flex-col text-left">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-central-borda shrink-0">
                              <span className="text-sm font-semibold text-slate-200 truncate pr-4">{item.orgaoNome || item.numeroCompra || 'Licitação'}</span>
                              <button onClick={() => setExpandedItemId(null)} className="text-slate-500 hover:text-slate-300 text-xs whitespace-nowrap">Fechar ✕</button>
                            </div>
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                            {/* Itens da licitação (procurement_compra_items) — só ComprasNet.
                                BLL/PCP: usuário quer só as mensagens, sem os itens do edital. */}
                            {item.portalOrigem !== 'bll' && item.portalOrigem !== 'pcp' &&
                              Array.isArray(item.compraItens) && item.compraItens.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-slate-200 mb-2">
                                  Itens da licitação
                                </h4>
                                <div className="space-y-1">
                                  {item.compraItens.map((sub: CompraItem) => {
                                    const badge = getCompraItemBadge(sub);
                                    return (
                                      <div
                                        key={sub.numero}
                                        className="flex items-center gap-2 py-1.5 px-2 rounded bg-central-elevado/60 hover:bg-central-elevado transition-colors"
                                      >
                                        <span className="text-slate-500 text-xs whitespace-nowrap">
                                          Item {sub.numero}:
                                        </span>
                                        <span className="flex-1 truncate text-sm text-slate-300">
                                          {sub.descricao || '—'}
                                        </span>
                                        <StatusBadge label={badge.label} color={badge.color} />
                                        {sub.propostaClassificacao !== undefined &&
                                          sub.propostaClassificacao !== null && (
                                            <span className="text-xs text-slate-500 whitespace-nowrap">
                                              {sub.propostaClassificacao}º
                                            </span>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <h4 className="text-sm font-medium text-slate-200">Mensagens</h4>
                                {item.lastMonitoredAt && (
                                  <span className="text-[10px] text-slate-500">
                                    Ultima verificacao: {fmtDateTime(item.lastMonitoredAt)}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={(e) => handleFetchMessages(e, item)}
                                disabled={fetchingMessagesId === item.id}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 rounded-lg text-xs transition-colors"
                              >
                                <Icon name="refresh" className={`w-3.5 h-3.5 ${fetchingMessagesId === item.id ? 'animate-spin' : ''}`} />
                                {fetchingMessagesId === item.id ? `Buscando... (${fetchElapsed}s)` : 'Buscar Mensagens'}
                              </button>
                            </div>

                            {messagesLoading ? (
                              <div className="text-center py-8 text-slate-400 animate-pulse text-sm">Carregando mensagens...</div>
                            ) : messages.length === 0 ? (
                              <div className="text-center py-8 text-slate-500">
                                <Icon name="mail" className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Nenhuma mensagem encontrada</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {messages.map(renderMessageCard)}
                              </div>
                            )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ==================== Import Modal ==================== */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-central-cartao rounded-central border border-central-borda w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-central-borda">
              <h3 className="text-lg font-semibold text-white">Importar do Banco de Licitacoes</h3>
              <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-white">
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-central-borda space-y-3">
              <div className="flex gap-1 bg-central-fundo rounded-lg p-1">
                <button
                  onClick={() => { setImportTab('novos'); setImportSelected(new Set()); }}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    importTab === 'novos' ? 'bg-central-elevado text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Novos {importItems.length > 0 && `(${importItems.length})`}
                </button>
                <button
                  onClick={() => { setImportTab('propostas'); setImportSelected(new Set()); }}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    importTab === 'propostas' ? 'bg-central-elevado text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Com Propostas {importProposalItems.length > 0 && `(${importProposalItems.length})`}
                </button>
              </div>
              <div className="relative">
                <Icon name="search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar por orgao ou objeto..."
                  value={importSearch}
                  onChange={e => setImportSearch(e.target.value)}
                  className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {importLoading ? (
                <div className="text-center py-12 text-slate-400 animate-pulse">Carregando...</div>
              ) : filteredImportItems.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  {activeImportList.length === 0
                    ? importTab === 'novos'
                      ? 'Nenhum item com status "Novo" no banco'
                      : 'Nenhuma proposta fora do monitoramento'
                    : 'Nenhum item encontrado'}
                </div>
              ) : (
                filteredImportItems.map(item => {
                  const stClass = STATUS_COLORS[item.status] || 'bg-slate-600/50 text-slate-300';
                  return (
                    <label
                      key={item.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-central-elevado cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={importSelected.has(item.id)}
                        onChange={() => toggleImportSelect(item.id)}
                        className="mt-1 rounded border-slate-600 bg-slate-900 text-primary focus:ring-primary"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-200 truncate">{getOrgaoDisplay(item) || '\u2014'}</p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{(item.objetoCompra || '').substring(0, 100)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${stClass}`}>
                            {(item.status || 'NOVO').toUpperCase()}
                          </span>
                          {item._proposalStatus && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-600/30 text-slate-300">
                              Proposta: {item._proposalStatus}
                            </span>
                          )}
                          {item.modalidadeNome && (
                            <span className="text-[10px] text-slate-500">{item.modalidadeNome}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between p-4 border-t border-central-borda">
              <span className="text-xs text-slate-500">
                {importSelected.size > 0 ? `${importSelected.size} selecionado(s)` : ''}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImportSelected}
                  disabled={importSelected.size === 0 || importing}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                  {importing ? 'Importando...' : `Importar (${importSelected.size})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Cadastrar Modal ==================== */}
      {showCadastrarModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-central-cartao rounded-central border border-central-borda w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-central-borda">
              <h3 className="text-lg font-semibold text-white">Cadastrar Licitacao</h3>
              <button onClick={() => setShowCadastrarModal(false)} className="text-slate-400 hover:text-white">
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-central-borda">
              <button
                onClick={() => { setCadastrarTab('pncp'); setCadastrarError(''); }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${cadastrarTab === 'pncp' ? 'text-white border-b-2 border-white' : 'text-slate-400 hover:text-slate-200'}`}
              >
                Buscar no PNCP
              </button>
              <button
                onClick={() => { setCadastrarTab('manual'); setCadastrarError(''); }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${cadastrarTab === 'manual' ? 'text-white border-b-2 border-white' : 'text-slate-400 hover:text-slate-200'}`}
              >
                Cadastro Manual
              </button>
            </div>

            {cadastrarTab === 'pncp' ? (
              <>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Cole o link da licitacao</label>
                    <input
                      type="text"
                      value={cadastrarUrl}
                      onChange={e => handleCadastrarUrlChange(e.target.value)}
                      placeholder="URL do PNCP, ComprasNet, BLL, BNC..."
                      className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                    />
                    {cadastrarUrl && isPortalUrl(cadastrarUrl) && !isPncpUrl(cadastrarUrl) && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-600/30 text-slate-300">
                          ComprasNet detectado
                        </span>
                        <span className="text-xs text-slate-500">Os dados serao buscados automaticamente no PNCP</span>
                      </div>
                    )}
                  </div>

                  {!(cadastrarUrl && isPortalUrl(cadastrarUrl) && !isPncpUrl(cadastrarUrl)) && (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 border-t border-central-borda" />
                        <span className="text-xs text-slate-500">ou preencha manualmente</span>
                        <div className="flex-1 border-t border-central-borda" />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">CNPJ do Orgao</label>
                          <input
                            type="text"
                            value={cadastrarCnpj}
                            onChange={e => { setCadastrarCnpj(e.target.value); setCadastrarError(''); }}
                            placeholder="00000000000000"
                            maxLength={14}
                            className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Ano</label>
                          <input
                            type="number"
                            value={cadastrarAno}
                            onChange={e => { setCadastrarAno(e.target.value); setCadastrarError(''); }}
                            className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Sequencial</label>
                          <input
                            type="number"
                            value={cadastrarSeq}
                            onChange={e => { setCadastrarSeq(e.target.value); setCadastrarError(''); }}
                            placeholder="1"
                            className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {cadastrarError && (
                    <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
                      {cadastrarError}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-central-borda">
                  <button
                    onClick={() => setShowCadastrarModal(false)}
                    className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCadastrar}
                    disabled={cadastrarLoading || (!(cadastrarUrl && isPortalUrl(cadastrarUrl) && !isPncpUrl(cadastrarUrl)) && (!cadastrarCnpj || !cadastrarSeq))}
                    className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                  >
                    {cadastrarLoading ? 'Buscando...' : 'Buscar e Importar'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 space-y-4">
                  <div className="text-xs text-slate-500 bg-central-fundo rounded-lg px-3 py-2">
                    Para licitacoes que nao estao no PNCP (ex: ComprasNet de Guarulhos). O item sera criado com status "participando" e monitoramento ativo.
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Portal</label>
                      <div className="w-full px-3 py-2 bg-central-fundo border border-central-borda rounded-lg text-sm text-slate-200">
                        Compras.gov.br
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Modalidade *</label>
                      <select
                        value={manualModalidade}
                        onChange={e => setManualModalidade(e.target.value as 'pe' | 'de')}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                      >
                        <option value="pe">Pregao Eletronico</option>
                        <option value="de">Dispensa Eletronica</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">UASG *</label>
                      <input
                        type="text"
                        value={manualUasg}
                        onChange={e => { setManualUasg(e.target.value.replace(/\D/g, '')); setCadastrarError(''); }}
                        onBlur={handleManualUasgBlur}
                        placeholder="928347"
                        maxLength={10}
                        className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">N. Compra *</label>
                      <input
                        type="text"
                        value={manualNumero}
                        onChange={e => handleManualNumeroChange(e.target.value)}
                        onBlur={handleManualNumeroBlur}
                        placeholder="90010/2025 ou 900102025"
                        className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                      />
                    </div>
                  </div>

                  {manualLookupLoading && (
                    <div className="text-xs text-slate-400 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Buscando dados no PNCP...
                    </div>
                  )}

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Orgao</label>
                    <input
                      type="text"
                      value={manualOrgao}
                      onChange={e => setManualOrgao(e.target.value)}
                      placeholder="Preenchido automaticamente pela UASG"
                      className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Descricao</label>
                    <input
                      type="text"
                      value={manualDescricao}
                      onChange={e => setManualDescricao(e.target.value)}
                      placeholder="Preenchido automaticamente pelo PNCP"
                      className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Valor Estimado</label>
                      <input
                        type="number"
                        step="0.01"
                        value={manualValor}
                        onChange={e => setManualValor(e.target.value)}
                        placeholder="0,00"
                        className={`w-full px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Abertura</label>
                      <input
                        type="datetime-local"
                        value={manualAbertura}
                        onChange={e => setManualAbertura(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Encerramento</label>
                      <input
                        type="datetime-local"
                        value={manualEncerramento}
                        onChange={e => setManualEncerramento(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Processo SEI / Administrativo</label>
                      <input
                        type="text"
                        value={manualProcessoSei}
                        onChange={e => setManualProcessoSei(e.target.value)}
                        placeholder="200.083808/2025"
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Criterio de Julgamento</label>
                      <input
                        type="text"
                        value={manualCriterio}
                        onChange={e => setManualCriterio(e.target.value)}
                        placeholder="MENOR PREÇO"
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="manual-srp"
                      checked={manualSrp}
                      onChange={e => setManualSrp(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-primary focus:ring-primary focus:ring-offset-0"
                    />
                    <label htmlFor="manual-srp" className="text-xs text-slate-400">Sistema de Registro de Precos (SRP)</label>
                  </div>

                  {cadastrarError && (
                    <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
                      {cadastrarError}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-central-borda">
                  <button
                    onClick={() => setShowCadastrarModal(false)}
                    className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCadastrarManual}
                    disabled={cadastrarLoading || !manualUasg || !manualNumero}
                    className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                  >
                    {cadastrarLoading ? 'Cadastrando...' : 'Cadastrar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bug HH: Modal de progresso do Verificar */}
      {batchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={(e) => { if (e.target === e.currentTarget && batchStatus?.finishedAt) { setBatchModal(null); setBatchStatus(null); setChecking(false); } }}>
          <div className="bg-central-cartao border border-central-borda rounded-central shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-central-borda flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">Verificando licitações</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {batchStatus
                    ? `${batchStatus.done}/${batchStatus.total} processados`
                    : 'Iniciando…'}
                  {batchStatus?.finishedAt && ' • concluído'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {batchStatus?.finishedAt && (() => {
                  const okCount = (batchStatus.items || []).filter((i: any) => i.status === 'ok').length;
                  const noChatCount = (batchStatus.items || []).filter((i: any) => i.status === 'no_chat').length;
                  const noMsgsCount = (batchStatus.items || []).filter((i: any) => i.status === 'no_messages').length;
                  const failCount = (batchStatus.items || []).filter((i: any) => i.status === 'failed').length;
                  const totalSaved = (batchStatus.items || []).reduce((s: number, i: any) => s + (i.saved || 0), 0);
                  return (
                    <div className="text-right text-xs">
                      <div className="text-white font-semibold">{totalSaved} msg(s) nova(s)</div>
                      <div className="text-slate-400 mt-0.5">
                        {okCount} OK • {noMsgsCount} sem msg nova • {noChatCount} captcha rejeitado • {failCount} falhas
                      </div>
                    </div>
                  );
                })()}
                <button
                  onClick={() => {
                    setBatchModal(null);
                    setBatchStatus(null);
                    if (batchStatus?.finishedAt) setChecking(false);
                  }}
                  className="text-slate-400 hover:text-white p-1"
                  title={batchStatus?.finishedAt ? 'Fechar' : 'Fechar (verificação segue em background)'}
                >
                  ✕
                </button>
              </div>
            </div>
            {batchStatus && (
              <div className="px-5 py-2 border-b border-central-borda">
                <div className="h-2 bg-central-elevado rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${batchStatus.finishedAt ? 'bg-slate-400' : 'bg-primary'}`}
                    style={{ width: `${batchStatus.total ? (batchStatus.done / batchStatus.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {!batchStatus ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  <Icon name="refresh" className="w-6 h-6 mx-auto mb-2 animate-spin opacity-60" />
                  Aguardando início do batch…
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(batchStatus.items || []).map((it: any, idx: number) => {
                    const statusBadge = (() => {
                      switch (it.status) {
                        case 'ok': return { lbl: `${it.saved} nova(s)`, cls: 'bg-slate-600/30 text-slate-300' };
                        case 'no_messages': return { lbl: 'sem msg nova', cls: 'bg-slate-600/30 text-slate-400' };
                        case 'no_chat': return { lbl: 'captcha rejeitado', cls: 'bg-amber-500/20 text-amber-300' };
                        case 'failed': return { lbl: it.error?.slice(0, 30) || 'falha', cls: 'bg-red-500/20 text-red-300' };
                        case 'running': return { lbl: 'processando…', cls: 'bg-slate-600/30 text-slate-300' };
                        default: return { lbl: 'aguardando', cls: 'bg-slate-700/30 text-slate-500' };
                      }
                    })();
                    return (
                      <div key={idx} className="flex items-center justify-between gap-3 px-3 py-1.5 rounded bg-central-elevado/60 text-xs">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-slate-500 font-mono w-7 text-right">{idx + 1}</span>
                          <span className="text-slate-200 truncate" title={it.orgao}>{it.orgao}</span>
                          <span className="text-[10px] text-slate-500 font-mono">{it.compraId.slice(0, 8)}…</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${statusBadge.cls} whitespace-nowrap`}>
                          {statusBadge.lbl}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {batchStatus?.finishedAt && (
              <div className="px-5 py-3 border-t border-central-borda flex justify-end">
                <button
                  onClick={() => {
                    setBatchModal(null);
                    setBatchStatus(null);
                    setChecking(false);
                  }}
                  className="px-4 py-1.5 rounded bg-primary hover:bg-primary-strong text-white text-sm font-medium"
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringView;
